import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API_BASE } from "../api";
import { logEvent } from "../apiUtils";
import "bootstrap/dist/css/bootstrap.min.css";
import "font-awesome/css/font-awesome.min.css";
import { useView } from "../context/ViewContext";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import axios from 'axios';

const BaseLayout = ({ children }) => {
  const [toggleMenu, setToggleMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { authData, logout } = useAuth();
  const { updateViewID } = useView();
  const [unreadMessages, setUnreadMessages] = useState([]);
  const [showMessages, setShowMessages] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleToggleMenu = () => {
    setToggleMenu(!toggleMenu);
  };

  const handleNavLinkClick = () => {
    updateViewID('');
    setToggleMenu(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    setToggleMenu(false);
    setSearchQuery('');
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const handleCloseMessages = () => setShowMessages(false);
  const handleShowMessages = () => setShowMessages(true);

  const fetchMessages = async () => {
    if (!authData.isAuthenticated) return;
    try {
      const response = await axios.get(`${API_BASE}/user/unread-messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.status === 200) {
        const msgs = response.data;
        setUnreadMessages(msgs);
      }
    } catch (error) {
      console.error('Error fetching messages:', error.response?.data);
    }
  };

  useEffect(() => {
    if (!authData.isAuthenticated) return;
    fetchMessages();
    const interval = setInterval(fetchMessages, 60000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData.isAuthenticated, token]);

  useEffect(() => {
    if (!showUserMenu) return;
    const close = (e) => {
      if (!e.target.closest?.('.fp-user-dropdown-wrap')) setShowUserMenu(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showUserMenu]);

  const clearMessage = async (messageId) => {
    try {
      const response = await axios.post(
        `${API_BASE}/user/view-message/${messageId}`, 
        {}, 
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.status === 200) {
        setUnreadMessages(prevMessages => prevMessages.filter(msg => msg.id !== messageId));
      }
    } catch (error) {
      console.error('Error clearing message:', error.response?.data || 'Unknown error');
    }
  };

  const acceptFriendRequest = async (messageId, senderId) => {
    try {
      const response = await axios.post(`${API_BASE}/user/add-friend/${authData.user?.id}/${senderId}`);
      if (response.status === 200) {
        await axios.delete(`${API_BASE}/user/delete-friend-request/${messageId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Error accepting friend request:', error.response?.data || 'Unknown error');
    }
    setUnreadMessages(prevMessages => prevMessages.filter(msg => msg.id !== messageId));
  };

  const declineFriendRequest = async (messageId) => {
    try {
      const response = await axios.delete(`${API_BASE}/user/delete-friend-request/${messageId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.status === 200) {
        setUnreadMessages(prevMessages => prevMessages.filter(msg => msg.id !== messageId));
      }
    } catch (error) {
      console.error('Error declining friend request:', error.response?.data || 'Unknown error');
    }
  };

  const navigateToUserProfile = (userId) => {
    updateViewID(userId);
    navigate('/profile');
  };

  return (
    <>
      <nav className="navbar navbar-expand-lg sticky-top fp-navbar">
        <div className="container-fluid">
          <Link className="fp-brand" to="/home" onClick={handleNavLinkClick}>
            <span>FLAME</span><span className="brand-flame">PICKS</span>
          </Link>
          <button
            className="navbar-toggler"
            type="button"
            aria-controls="navbarNavAltMarkup"
            aria-expanded={toggleMenu}
            aria-label="Toggle navigation"
            onClick={handleToggleMenu}
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className={`navbar-collapse${toggleMenu ? ' nav-open' : ''}`} id="navbarNavAltMarkup">
            {/* Left nav links */}
            <div className="navbar-nav me-auto mb-2 mb-lg-0">
              {authData.isAuthenticated ? (
                <>
                  {/* Mobile-only search bar (top of collapsed menu) */}
                  <form className="fp-search-wrap d-lg-none" onSubmit={handleSearch}>
                    <i className="fa fa-search fp-search-icon"></i>
                    <input
                      className="fp-search-input"
                      type="text"
                      placeholder="Search members, teams, games..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </form>
                  <Link className="fp-nav-link" to="/home" onClick={handleNavLinkClick}>
                    <i className="fa fa-fw fa-home"></i> Home
                  </Link>
                  <Link className="fp-nav-link" to="/managepicks" onClick={handleNavLinkClick}>
                    <i className="fa fa-fw fa-history"></i> Manage Picks
                  </Link>
                  <Link className="fp-nav-link" to="/groups" onClick={handleNavLinkClick}>
                    <i className="fa fa-fw fa-sitemap"></i> Groups
                  </Link>
                  <Link className="fp-nav-link" to="/leaderboard" onClick={handleNavLinkClick}>
                    <i className="fa fa-fw fa-list"></i> Leaderboard
                  </Link>
                  {/* Achievements: mobile only (desktop accessible via user dropdown) */}
                  <Link className="fp-nav-link d-lg-none" to="/achievements" onClick={handleNavLinkClick}>
                    <i className="fa fa-fw fa-trophy"></i> Achievements
                  </Link>
                  {/* Inbox: mobile only */}
                  <Link className="fp-nav-link d-lg-none" to="/inbox" onClick={handleNavLinkClick}>
                    <i className="fa fa-fw fa-inbox"></i> Inbox
                  </Link>
                  {/* Settings: mobile only (desktop accessible via user dropdown) */}
                  <Link className="fp-nav-link d-lg-none" to="/settings" onClick={handleNavLinkClick}>
                    <i className="fa fa-fw fa-cogs"></i> Settings
                  </Link>
                  <Link className="fp-nav-link" to="/support" onClick={handleNavLinkClick}>
                    <i className="fa fa-fw fa-wrench"></i> Support
                  </Link>

                </>
              ) : (
                <>
                  <Link className="fp-nav-link" to="/login" onClick={() => setToggleMenu(false)}>
                    <i className="fa fa-fw fa-sign-in"></i> Login
                  </Link>
                  <Link className="fp-nav-link" to="/sign-up" onClick={() => setToggleMenu(false)}>
                    <i className="fa fa-fw fa-user-o"></i> Sign Up
                  </Link>
                </>
              )}
            </div>
            {/* Right items */}
            <div className="navbar-nav ms-auto d-flex align-items-center gap-1">
              {/* Desktop-only search bar */}
              {authData.isAuthenticated && (
                <form className="fp-search-wrap d-none d-lg-flex me-1" onSubmit={handleSearch}>
                  <i className="fa fa-search fp-search-icon"></i>
                  <input
                    className="fp-search-input"
                    type="text"
                    placeholder="Search members, teams, games..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </form>
              )}
              <a
                href="https://classic.flamepicks.com"
                className="fp-nav-link fp-nav-classic"
                onClick={async (e) => {
                  e.preventDefault();
                  const flagKey = 'event_classic_mode';
                  if (!localStorage.getItem(flagKey)) {
                    await logEvent('classic_mode');
                    localStorage.setItem(flagKey, '1');
                  }
                  window.location.href = 'https://classic.flamepicks.com';
                }}
              >
                <i className="fa fa-history"></i> Classic
              </a>
              {authData.isAuthenticated && (
                <>
                  <button className="fp-bell-btn bell-icon-container" onClick={handleShowMessages}>
                    <i className="fa fa-bell"></i>
                    {unreadMessages.length > 0 &&
                      <span className="notification-badge">
                        {unreadMessages.length}
                      </span>}
                  </button>
                  <Link
                    className="fp-bell-btn d-lg-none"
                    to="/friends"
                    onClick={handleNavLinkClick}
                    title="Friends"
                  >
                    <i className="fa fa-users"></i>
                  </Link>
                  {/* Desktop: username dropdown */}
                  <div className="fp-user-dropdown-wrap d-none d-lg-block">
                    <button
                      className="fp-user-dropdown-btn"
                      onClick={() => setShowUserMenu(v => !v)}
                    >
                      {authData.user?.firstName} {authData.user?.lastName}
                      <i className={`fa fa-chevron-${showUserMenu ? 'up' : 'down'} fp-user-caret`}></i>
                    </button>
                    {showUserMenu && (
                      <div className="fp-user-menu">
                        <Link
                          className="fp-user-menu-item"
                          to="/profile"
                          onClick={() => { handleNavLinkClick(); setShowUserMenu(false); }}
                        >
                          <i className="fa fa-fw fa-user"></i> Profile
                        </Link>
                        <Link
                          className="fp-user-menu-item"
                          to="/friends"
                          onClick={() => { handleNavLinkClick(); setShowUserMenu(false); }}
                        >
                          <i className="fa fa-fw fa-users"></i> Friends
                        </Link>
                        <Link
                          className="fp-user-menu-item"
                          to="/inbox"
                          onClick={() => { handleNavLinkClick(); setShowUserMenu(false); }}
                        >
                          <i className="fa fa-fw fa-inbox"></i> Inbox
                        </Link>
                        <Link
                          className="fp-user-menu-item"
                          to="/groups"
                          onClick={() => { handleNavLinkClick(); setShowUserMenu(false); }}
                        >
                          <i className="fa fa-fw fa-sitemap"></i> Groups
                        </Link>
                        <Link
                          className="fp-user-menu-item"
                          to="/achievements"
                          onClick={() => { handleNavLinkClick(); setShowUserMenu(false); }}
                        >
                          <i className="fa fa-fw fa-trophy"></i> Achievements
                        </Link>
                        <Link
                          className="fp-user-menu-item"
                          to="/settings"
                          onClick={() => { handleNavLinkClick(); setShowUserMenu(false); }}
                        >
                          <i className="fa fa-fw fa-cogs"></i> Settings
                        </Link>
                        <button
                          className="fp-user-menu-item fp-user-menu-logout"
                          onClick={() => { handleLogout(); setShowUserMenu(false); }}
                        >
                          <i className="fa fa-fw fa-sign-out"></i> Logout
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Mobile: username links to profile */}
                  <Link
                    className="fp-user-name d-lg-none"
                    to="/profile"
                    onClick={handleNavLinkClick}
                  >
                    {authData.user?.firstName} {authData.user?.lastName}
                  </Link>
                  {/* Logout: mobile only (desktop uses dropdown) */}
                  <button
                    className="fp-logout-btn d-lg-none"
                    onClick={() => { handleLogout(); setToggleMenu(false); }}
                  >
                    <i className="fa fa-fw fa-sign-out"></i> Logout
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>
      <Modal show={showMessages} onHide={handleCloseMessages}>
        <Modal.Header closeButton>
          <Modal.Title>Notifications</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {unreadMessages.length > 0 ? (
            <div className="message-list">
              {unreadMessages.map(message => {
                if (message.is_achievement) {
                  return (
                    <div key={message.id} className="message-item message-item--achievement">
                      <div className="message-header">
                        <i className="fa fa-trophy" style={{ color: 'var(--gold)', marginRight: 6 }} />
                        <span className="sender-name" style={{ color: 'var(--gold)' }}>Achievement Unlocked</span>
                      </div>
                      <div className="message-body">
                        <div className="message-content">{message.content}</div>
                        <div className="message-actions">
                          <button
                            onClick={() => { clearMessage(message.id); navigate(message.achievement_key ? `/achievements/${message.achievement_key}` : '/achievements'); handleCloseMessages(); }}
                            className="btn btn-sm btn-secondary"
                            style={{ marginRight: 5 }}
                          >
                            View
                          </button>
                          <button onClick={() => clearMessage(message.id)} className="btn btn-sm btn-danger">X</button>
                        </div>
                      </div>
                      <hr />
                    </div>
                  );
                }
                return (
                  <div key={message.id} className="message-item">
                    <div className="message-header">
                      <span onClick={() => navigateToUserProfile(message.sender_id)} className="sender-name clickable">
                        {message.sender_name}
                      </span>
                      {message.is_friend_request ? ' sent you a friend request' : ' sent you a message'}
                    </div>
                    <div className="message-body">
                      {!message.is_friend_request && <div className="message-content">"{message.content}"</div>}
                      <div className="message-actions">
                        {message.is_friend_request ? (
                          <>
                            <button onClick={() => acceptFriendRequest(message.id, message.sender_id)} className="btn btn-sm btn-success" style={{ marginRight: '5px' }}>
                              Accept
                            </button>
                            <button onClick={() => declineFriendRequest(message.id)} className="btn btn-sm btn-danger">
                              Decline
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => { clearMessage(message.id); navigate(`/inbox?tab=messages&with=${message.sender_id}`); handleCloseMessages(); }}
                              className="btn btn-sm btn-secondary"
                              style={{ marginRight: 5 }}
                            >
                              Reply
                            </button>
                            <button onClick={() => clearMessage(message.id)} className="btn btn-sm btn-danger">
                              X
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <hr />
                  </div>
                );
              })}
            </div>
          ) : <p>No unread messages</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => { handleCloseMessages(); navigate('/inbox'); }}
            style={{ background: 'var(--elevated)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'Montserrat, sans-serif', fontSize: '0.82rem', fontWeight: 700 }}
          >
            <i className="fa fa-inbox" style={{ marginRight: 6 }} /> View All
          </Button>
          <Button variant="secondary" onClick={handleCloseMessages}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <div className="container">{children}</div>
    </>
  );
};

export default BaseLayout;